import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import QrScanner from "../components/admin/attendance/QrScanner";
import {
  LOCAL_STORAGE_ADMIN_SECRET_KEY,
  listRegistrations,
  markAttendance,
  MarkAttendanceStatus,
} from "../utils/adminApi";
import { AdminRegistration } from "../types/admin";
import { extractRegistrationIdFromInput } from "../utils/attendanceQr";
import "./ItProAdminPage.css";
import "./ItProAttendancePage.css";

type AttendanceUiState =
  | "idle"
  | "scanner-starting"
  | "scanning"
  | "processing"
  | "success"
  | "warning"
  | "error";

type AttendanceResultState =
  | "success"
  | "already_checked_in"
  | "not-found"
  | "api-error"
  | "invalid-qr"
  | "camera-permission-denied";

type AttendanceSource = "qr" | "search";

interface ResultAttendeeInfo {
  name: string | null;
  email: string | null;
  phoneNumber: string | null;
  profession: string | null;
  organization: string | null;
  designation: string | null;
  linkedinUrl: string | null;
  tshirtSize: string | null;
  foodPreference: string | null;
  nic: string | null;
}

interface ResultRegistrationInfo {
  registrationStatus: string | null;
  isConfirmed: boolean | null;
}

interface AttendanceResultItem {
  id: string;
  status: AttendanceResultState;
  registrationId: string | null;
  message: string;
  manualApprovalApplied: boolean;
  scannedAt: number;
  attended: boolean | null;
  attendedAt: string | null;
  attendee: ResultAttendeeInfo;
  registration: ResultRegistrationInfo;
}

interface LastScanRecord {
  value: string;
  at: number;
}

const SCAN_COOLDOWN_MS = 4000;
const AUTO_SCAN_RESUME_DELAY_MS = 2500;
const SEARCH_DEBOUNCE_MS = 300;
const MAX_RECENT_SCANS = 10;
const MAX_SEARCH_RESULTS = 12;
const INVALID_OR_NOT_FOUND_MESSAGE =
  "Registration not found or not eligible for attendance marking.";
const INVALID_OR_NOT_FOUND_HELPER = "Please try another QR code or use attendee search.";
const ALL_TSHIRT_SIZES = "all";
const ALL_FOOD_PREFERENCES = "all";
const DEFAULT_TSHIRT_SIZE_OPTIONS = ["XS", "S", "M", "L", "XL", "XXL"];
const DEFAULT_FOOD_PREFERENCE_OPTIONS = ["veg", "non-veg"];
const FOOD_PREFERENCE_LABELS: Record<string, string> = {
  veg: "Vegetarian",
  "non-veg": "Non-Vegetarian",
};
const GUEST_CSV_EXPORT_HEADERS = [
  "Name",
  "Email",
  "Phone Number",
  "Approval Status",
  "Confirmation Status",
  "Check-in Time",
  "Attendance Status",
  "T-shirt size",
  "Food Preference",
  "NIC",
  "Profession",
  "Organization",
  "Designation",
  "LinkedIn Profile URL",
];

const getPublicAssetPath = (relativePath: string) => {
  const basePath = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  const normalizedPath = relativePath.replace(/^\/+/, "");

  return `${basePath}/${normalizedPath}`;
};

const parseTimestamp = (value: string | null | undefined, fallback: number) => {
  if (!value) {
    return fallback;
  }

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? fallback : parsed;
};

type RecentTrackableStatus = "success" | "already_checked_in" | "not-found" | "api-error";

const STATUS_META: Record<
  AttendanceResultState,
  { label: string; className: string; icon: string }
> = {
  success: {
    label: "Success",
    className: "attendance-result-success",
    icon: "bi-check-circle",
  },
  already_checked_in: {
    label: "Already Checked In",
    className: "attendance-result-already-attended",
    icon: "bi-person-check",
  },
  "invalid-qr": {
    label: "Invalid QR",
    className: "attendance-result-invalid-qr",
    icon: "bi-upc-scan",
  },
  "not-found": {
    label: "Not Found",
    className: "attendance-result-not-found",
    icon: "bi-search",
  },
  "api-error": {
    label: "API Error",
    className: "attendance-result-api-error",
    icon: "bi-exclamation-triangle",
  },
  "camera-permission-denied": {
    label: "Camera Denied",
    className: "attendance-result-camera-permission-denied",
    icon: "bi-camera-video-off",
  },
};

const UI_STATE_META: Record<AttendanceUiState, { label: string; className: string }> = {
  idle: {
    label: "Idle",
    className: "attendance-state-idle",
  },
  "scanner-starting": {
    label: "Scanner Starting",
    className: "attendance-state-scanner-starting",
  },
  scanning: {
    label: "Scanning",
    className: "attendance-state-scanning",
  },
  processing: {
    label: "Processing",
    className: "attendance-state-processing",
  },
  success: {
    label: "Success",
    className: "attendance-state-success",
  },
  warning: {
    label: "Warning",
    className: "attendance-state-warning",
  },
  error: {
    label: "Error",
    className: "attendance-state-error",
  },
};

const RESULT_CARD_META: Record<
  AttendanceResultState,
  {
    title: string;
    icon: string;
    defaultMessage: string;
    helperText?: string;
    cardClassName: string;
  }
> = {
  success: {
    title: "Attendance Marked",
    icon: "bi-check2-circle",
    defaultMessage: "Attendance marked successfully.",
    cardClassName: "attendance-result-card-success",
  },
  already_checked_in: {
    title: "Already Checked In",
    icon: "bi-person-check",
    defaultMessage: "This attendee has already been marked as attended.",
    cardClassName: "attendance-result-card-warning",
  },
  "not-found": {
    title: "Invalid QR",
    icon: "bi-x-octagon",
    defaultMessage: INVALID_OR_NOT_FOUND_MESSAGE,
    helperText: INVALID_OR_NOT_FOUND_HELPER,
    cardClassName: "attendance-result-card-error",
  },
  "api-error": {
    title: "API Error",
    icon: "bi-exclamation-triangle",
    defaultMessage: "Unable to mark attendance right now. Please try again.",
    cardClassName: "attendance-result-card-error",
  },
  "invalid-qr": {
    title: "Invalid QR",
    icon: "bi-upc-scan",
    defaultMessage: INVALID_OR_NOT_FOUND_MESSAGE,
    helperText: INVALID_OR_NOT_FOUND_HELPER,
    cardClassName: "attendance-result-card-error",
  },
  "camera-permission-denied": {
    title: "Camera Access Required",
    icon: "bi-camera-video-off",
    defaultMessage: "Camera access is required to scan QR codes",
    cardClassName: "attendance-result-card-error",
  },
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
};

const asNonEmptyText = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
};

const asOptionalTrimmedText = (value: string | null | undefined): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
};

const normalizeKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

const findNameInPayload = (value: unknown, depth = 0): string | null => {
  if (depth > 5) {
    return null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findNameInPayload(item, depth + 1);
      if (found) {
        return found;
      }
    }

    return null;
  }

  const record = asRecord(value);
  if (!record) {
    return null;
  }

  for (const [rawKey, rawValue] of Object.entries(record)) {
    const normalized = normalizeKey(rawKey);
    if (!["name", "fullname", "attendeename", "participantname"].includes(normalized)) {
      continue;
    }

    const text = asNonEmptyText(rawValue);
    if (text) {
      return text;
    }
  }

  for (const nested of Object.values(record)) {
    const found = findNameInPayload(nested, depth + 1);
    if (found) {
      return found;
    }
  }

  return null;
};

const findOrganizationInPayload = (value: unknown, depth = 0): string | null => {
  if (depth > 5) {
    return null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findOrganizationInPayload(item, depth + 1);
      if (found) {
        return found;
      }
    }

    return null;
  }

  const record = asRecord(value);
  if (!record) {
    return null;
  }

  for (const [rawKey, rawValue] of Object.entries(record)) {
    const normalized = normalizeKey(rawKey);
    if (
      ![
        "organization",
        "organizationname",
        "company",
        "companyname",
        "institution",
        "institute",
        "university",
        "college",
        "employer",
        "workplace",
      ].includes(normalized)
    ) {
      continue;
    }

    const text = asNonEmptyText(rawValue);
    if (text) {
      return text;
    }
  }

  for (const nested of Object.values(record)) {
    const found = findOrganizationInPayload(nested, depth + 1);
    if (found) {
      return found;
    }
  }

  return null;
};

const toResultStatus = (
  status: MarkAttendanceStatus,
): Exclude<AttendanceResultState, "invalid-qr" | "camera-permission-denied"> => {
  if (status === "success") {
    return "success";
  }

  if (status === "already-attended") {
    return "already_checked_in";
  }

  if (status === "not-found") {
    return "not-found";
  }

  return "api-error";
};

const toUiState = (
  status: AttendanceResultState,
): Exclude<AttendanceUiState, "idle" | "scanner-starting" | "scanning" | "processing"> => {
  if (status === "success") {
    return "success";
  }

  if (status === "already_checked_in") {
    return "warning";
  }

  return "error";
};

const isTrackableRecentStatus = (
  status: AttendanceResultState,
): status is RecentTrackableStatus => {
  return ["success", "already_checked_in", "not-found", "api-error"].includes(status);
};

const formatSriLankaTime = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const date = typeof value === "number" ? new Date(value) : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-LK", {
    timeZone: "Asia/Colombo",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const formatRegistrationStatus = (value: string | null | undefined) => {
  if (!value) {
    return "-";
  }

  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatConfirmationStatus = (value: boolean | null | undefined) => {
  if (value === true) {
    return "Confirmed";
  }

  if (value === false) {
    return "Not Confirmed";
  }

  return "Unknown";
};

const formatAttendanceStatus = (value: boolean | null | undefined) => {
  if (value === true) {
    return "Attended";
  }

  if (value === false) {
    return "Not Attended";
  }

  return "Unknown";
};

const normalizeTshirtSize = (value: string | null | undefined) => {
  const trimmed = asOptionalTrimmedText(value);
  if (!trimmed) {
    return "";
  }

  return trimmed.toUpperCase();
};

const normalizeFoodPreference = (value: string | null | undefined) => {
  const trimmed = asOptionalTrimmedText(value);
  if (!trimmed) {
    return "";
  }

  const normalized = trimmed.toLowerCase().replace(/[_\s]+/g, "-");

  if (normalized.includes("non") && normalized.includes("veg")) {
    return "non-veg";
  }

  if (normalized.includes("veg")) {
    return "veg";
  }

  return normalized;
};

const buildGuestFilterOptions = (values: Array<string | null | undefined>) => {
  const optionSet = new Set<string>();

  values.forEach((value) => {
    const normalized = normalizeFoodPreference(value);
    if (!normalized) {
      return;
    }

    optionSet.add(normalized);
  });

  return Array.from(optionSet).sort((left, right) => {
    return left.localeCompare(right, undefined, { sensitivity: "base" });
  });
};

const mergeFilterOptions = (defaults: string[], dynamic: string[]) => {
  const merged = [...defaults, ...dynamic];
  const mergedSet = new Set<string>();

  return merged.filter((value) => {
    const normalized = asOptionalTrimmedText(value);
    if (!normalized || mergedSet.has(normalized)) {
      return false;
    }

    mergedSet.add(normalized);
    return true;
  });
};

const formatFoodPreference = (value: string | null | undefined) => {
  const normalized = normalizeFoodPreference(value);

  if (!normalized) {
    return "-";
  }

  return FOOD_PREFERENCE_LABELS[normalized] || normalized;
};

const formatSriLankaTimeForCsv = (value: string | number | null | undefined) => {
  const formatted = formatSriLankaTime(value);
  return formatted === "-" ? "" : formatted;
};

const formatRegistrationStatusForCsv = (value: string | null | undefined) => {
  if (!asOptionalTrimmedText(value)) {
    return "";
  }

  return formatRegistrationStatus(value);
};

const formatConfirmationStatusForCsv = (value: boolean | null | undefined) => {
  if (value === null || value === undefined) {
    return "";
  }

  return formatConfirmationStatus(value);
};

const formatAttendanceStatusForCsv = (value: boolean | null | undefined) => {
  if (value === null || value === undefined) {
    return "";
  }

  return formatAttendanceStatus(value);
};

const formatCsvValue = (value: unknown) => {
  if (value === undefined || value === null) {
    return '""';
  }

  const escapedValue = String(value).replace(/"/g, '""');
  return `"${escapedValue}"`;
};

const ItProAttendancePage = () => {
  const [adminSecretPresent, setAdminSecretPresent] = useState(false);
  const [secretInput, setSecretInput] = useState("");
  const [secretError, setSecretError] = useState<string | null>(null);
  const [isScannerActive, setIsScannerActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [scannerMessage, setScannerMessage] = useState<string | null>(null);
  const [currentResult, setCurrentResult] = useState<AttendanceResultItem | null>(null);
  const [recentScans, setRecentScans] = useState<AttendanceResultItem[]>([]);
  const [uiState, setUiState] = useState<AttendanceUiState>("idle");
  const [allRegistrations, setAllRegistrations] = useState<AdminRegistration[]>([]);
  const [isLoadingRegistrations, setIsLoadingRegistrations] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [guestSearchTerm, setGuestSearchTerm] = useState("");
  const [guestTshirtFilter, setGuestTshirtFilter] = useState(ALL_TSHIRT_SIZES);
  const [guestFoodPreferenceFilter, setGuestFoodPreferenceFilter] =
    useState(ALL_FOOD_PREFERENCES);
  const [searchActionRegistrationId, setSearchActionRegistrationId] = useState<
    string | null
  >(null);
  const [selectedSearchRegistration, setSelectedSearchRegistration] =
    useState<AdminRegistration | null>(null);

  const processingLockRef = useRef(false);
  const releaseLockTimeoutRef = useRef<number | null>(null);
  const lastQrValueRef = useRef<LastScanRecord | null>(null);
  const lastProcessedValueRef = useRef<LastScanRecord | null>(null);
  const attendedRegistryRef = useRef<
    Map<
      string,
      {
        attendee: ResultAttendeeInfo;
        attendedAt: string | null;
        registration: ResultRegistrationInfo;
        registrationId: string | null;
      }
    >
  >(new Map());

  useEffect(() => {
    const images = ["bg.jpg", "bg2.png"];
    const randomImage = images[Math.floor(Math.random() * images.length)];
    const pageEl = document.getElementById("itpro-attendance-page");

    if (pageEl) {
      pageEl.style.background = `url('${getPublicAssetPath(
        `assets/img/${randomImage}`,
      )}') top center / cover no-repeat`;
    }
  }, []);

  useEffect(() => {
    const storedSecret = window.localStorage.getItem(LOCAL_STORAGE_ADMIN_SECRET_KEY);
    if (storedSecret?.trim()) {
      setAdminSecretPresent(true);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (releaseLockTimeoutRef.current !== null) {
        window.clearTimeout(releaseLockTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (!selectedSearchRegistration) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedSearchRegistration(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedSearchRegistration]);

  useEffect(() => {
    if (!selectedSearchRegistration?.registration_id) {
      return;
    }

    const refreshedRegistration = allRegistrations.find(
      (registration) =>
        registration.registration_id === selectedSearchRegistration.registration_id,
    );

    if (
      refreshedRegistration &&
      refreshedRegistration !== selectedSearchRegistration
    ) {
      setSelectedSearchRegistration(refreshedRegistration);
    }
  }, [allRegistrations, selectedSearchRegistration]);

  const invalidateStoredSecret = useCallback((message: string) => {
    const lowered = message.toLowerCase();

    if (!lowered.includes("forbidden") && !lowered.includes("unauthorized")) {
      return;
    }

    window.localStorage.removeItem(LOCAL_STORAGE_ADMIN_SECRET_KEY);
    setAdminSecretPresent(false);
    setIsScannerActive(false);
    setSecretError("Saved admin secret is invalid. Please enter it again.");
  }, []);

  useEffect(() => {
    if (!adminSecretPresent) {
      return;
    }

    let isCancelled = false;

    const loadRegistrations = async () => {
      setIsLoadingRegistrations(true);
      setSearchError(null);

      try {
        const registrations = await listRegistrations();

        if (!isCancelled) {
          setAllRegistrations(registrations);
        }
      } catch (error) {
        if (isCancelled) {
          return;
        }

        const message = getErrorMessage(
          error,
          "Unable to load attendee records for manual search.",
        );

        invalidateStoredSecret(message);
        setSearchError(message);
      } finally {
        if (!isCancelled) {
          setIsLoadingRegistrations(false);
        }
      }
    };

    void loadRegistrations();

    return () => {
      isCancelled = true;
    };
  }, [adminSecretPresent, invalidateStoredSecret]);

  const registrationsById = useMemo(() => {
    const map = new Map<string, AdminRegistration>();

    allRegistrations.forEach((registration) => {
      if (registration.registration_id) {
        map.set(registration.registration_id, registration);
      }
    });

    return map;
  }, [allRegistrations]);

  const searchResults = useMemo(() => {
    const normalized = debouncedSearchTerm.toLowerCase();
    if (!normalized) {
      return [];
    }

    return allRegistrations
      .filter((registration) => {
        const name = registration.name.toLowerCase();

        return name.includes(normalized);
      })
      .slice(0, MAX_SEARCH_RESULTS);
  }, [allRegistrations, debouncedSearchTerm]);

  const appendRecentScan = useCallback((item: AttendanceResultItem) => {
    setRecentScans((current) => {
      const duplicateIndex = current.findIndex((existing) => {
        if (!existing.registrationId || !item.registrationId) {
          return false;
        }

        return existing.registrationId === item.registrationId;
      });

      if (duplicateIndex === 0) {
        return [{ ...current[0], ...item, id: current[0].id }, ...current.slice(1)].slice(
          0,
          MAX_RECENT_SCANS,
        );
      }

      if (duplicateIndex > 0) {
        const existing = current[duplicateIndex];
        const merged = { ...existing, ...item, id: existing.id };
        const remaining = current.filter((_, index) => index !== duplicateIndex);

        return [merged, ...remaining].slice(0, MAX_RECENT_SCANS);
      }

      return [item, ...current].slice(0, MAX_RECENT_SCANS);
    });
  }, []);

  const processAttendance = useCallback(
    async (
      registrationId: string,
      source: AttendanceSource,
      preferredName?: string | null,
    ) => {
      const normalizedRegistrationId = registrationId.trim();
      if (!normalizedRegistrationId) {
        return;
      }

      const now = Date.now();
      const lastProcessed = lastProcessedValueRef.current;

      if (processingLockRef.current) {
        return;
      }

      if (
        lastProcessed &&
        lastProcessed.value === normalizedRegistrationId &&
        now - lastProcessed.at < SCAN_COOLDOWN_MS
      ) {
        return;
      }

      processingLockRef.current = true;
      lastProcessedValueRef.current = {
        value: normalizedRegistrationId,
        at: now,
      };

      setIsProcessing(true);
      setUiState("processing");
      setScannerMessage(null);

      try {
        let preScannedRegistration = registrationsById.get(normalizedRegistrationId);
        const isPendingApprovalStatus =
          preScannedRegistration?.status?.trim().toLowerCase() === "pending";
        const isNotConfirmed = preScannedRegistration?.is_confirmed !== true;
        const shouldApplyManualApproval =
          source === "search" &&
          Boolean(preScannedRegistration) &&
          isPendingApprovalStatus &&
          isNotConfirmed;

        const apiResult = shouldApplyManualApproval
          ? await markAttendance(normalizedRegistrationId, {
              source: "manual-search",
              autoApprovePending: true,
              autoConfirmPending: true,
              skipConfirmationEmail: true,
              skipAttendanceEmail: true,
              suppressAllEmails: true,
            })
          : await markAttendance(normalizedRegistrationId);
        let status = toResultStatus(apiResult.status);
        const resolvedRegistrationId =
          apiResult.registration?.id || apiResult.registrationId || normalizedRegistrationId;
        const resolvedRegistration = registrationsById.get(resolvedRegistrationId);
        let resolvedAttendee: ResultAttendeeInfo = {
          name:
            preferredName?.trim() ||
            resolvedRegistration?.name.trim() ||
            findNameInPayload(apiResult.payload),
          email: resolvedRegistration?.email?.trim() || null,
          phoneNumber: resolvedRegistration?.phone_number?.trim() || null,
          profession: resolvedRegistration?.profession?.trim() || null,
          organization:
            resolvedRegistration?.organization?.trim() ||
            findOrganizationInPayload(apiResult.payload),
          designation: resolvedRegistration?.designation?.trim() || null,
          linkedinUrl: resolvedRegistration?.linkedin_url?.trim() || null,
          tshirtSize: resolvedRegistration?.tshirt_size?.trim() || null,
          foodPreference: resolvedRegistration?.food_preference?.trim() || null,
          nic: resolvedRegistration?.id_number?.trim() || null,
        };
        let resolvedRegistrationInfo: ResultRegistrationInfo = {
          registrationStatus:
            asOptionalTrimmedText(apiResult.registration?.status) ||
            asOptionalTrimmedText(resolvedRegistration?.status) ||
            asOptionalTrimmedText(preScannedRegistration?.status),
          isConfirmed:
            apiResult.registration?.is_confirmed ??
            resolvedRegistration?.is_confirmed ??
            preScannedRegistration?.is_confirmed ??
            null,
        };
        let attended =
          apiResult.registration?.attended ??
          (status === "success" || status === "already_checked_in" ? true : null);
        let attendedAt =
          apiResult.registration?.attended_at || resolvedRegistration?.attended_at || null;

        const knownAttendee =
          attendedRegistryRef.current.get(resolvedRegistrationId) ||
          attendedRegistryRef.current.get(normalizedRegistrationId);

        const hasAttendedRegistrationRecord =
          apiResult.registration?.attended === true ||
          preScannedRegistration?.attended === true ||
          resolvedRegistration?.attended === true ||
          knownAttendee?.registrationId === resolvedRegistrationId ||
          knownAttendee?.registrationId === normalizedRegistrationId;

        const hasKnownAttendedRecord =
          Boolean(knownAttendee) ||
          preScannedRegistration?.attended === true ||
          resolvedRegistration?.attended === true;

        const shouldMapToAlreadyCheckedIn =
          hasAttendedRegistrationRecord && (status === "not-found" || status === "api-error");

        const shouldTreatSuccessAsAlreadyCheckedIn =
          status === "success" &&
          hasKnownAttendedRecord &&
          (preScannedRegistration?.attended === true ||
            resolvedRegistration?.attended === true ||
            Boolean(knownAttendee));

        if (shouldMapToAlreadyCheckedIn || shouldTreatSuccessAsAlreadyCheckedIn) {
          status = "already_checked_in";
          attended = true;
          attendedAt =
            attendedAt ||
            knownAttendee?.attendedAt ||
            preScannedRegistration?.attended_at ||
            resolvedRegistration?.attended_at ||
            null;
          resolvedAttendee = {
            name:
              resolvedAttendee.name ||
              knownAttendee?.attendee.name ||
              preScannedRegistration?.name ||
              resolvedRegistration?.name ||
              null,
            email:
              resolvedAttendee.email ||
              knownAttendee?.attendee.email ||
              preScannedRegistration?.email ||
              resolvedRegistration?.email ||
              null,
            phoneNumber:
              resolvedAttendee.phoneNumber ||
              knownAttendee?.attendee.phoneNumber ||
              preScannedRegistration?.phone_number ||
              resolvedRegistration?.phone_number ||
              null,
            profession:
              resolvedAttendee.profession ||
              knownAttendee?.attendee.profession ||
              preScannedRegistration?.profession ||
              resolvedRegistration?.profession ||
              null,
            organization:
              resolvedAttendee.organization ||
              knownAttendee?.attendee.organization ||
              preScannedRegistration?.organization ||
              resolvedRegistration?.organization ||
              null,
            designation:
              resolvedAttendee.designation ||
              knownAttendee?.attendee.designation ||
              preScannedRegistration?.designation ||
              resolvedRegistration?.designation ||
              null,
            linkedinUrl:
              resolvedAttendee.linkedinUrl ||
              knownAttendee?.attendee.linkedinUrl ||
              preScannedRegistration?.linkedin_url ||
              resolvedRegistration?.linkedin_url ||
              null,
            tshirtSize:
              resolvedAttendee.tshirtSize ||
              knownAttendee?.attendee.tshirtSize ||
              preScannedRegistration?.tshirt_size ||
              resolvedRegistration?.tshirt_size ||
              null,
            foodPreference:
              resolvedAttendee.foodPreference ||
              knownAttendee?.attendee.foodPreference ||
              preScannedRegistration?.food_preference ||
              resolvedRegistration?.food_preference ||
              null,
            nic:
              resolvedAttendee.nic ||
              knownAttendee?.attendee.nic ||
              preScannedRegistration?.id_number ||
              resolvedRegistration?.id_number ||
              null,
          };

          resolvedRegistrationInfo = {
            registrationStatus:
              resolvedRegistrationInfo.registrationStatus ||
              knownAttendee?.registration.registrationStatus ||
              null,
            isConfirmed:
              resolvedRegistrationInfo.isConfirmed ??
              knownAttendee?.registration.isConfirmed ??
              null,
          };
        }

        const manualApprovalApplied =
          shouldApplyManualApproval &&
          (status === "success" || status === "already_checked_in");

        if (manualApprovalApplied) {
          resolvedRegistrationInfo = {
            registrationStatus: resolvedRegistrationInfo.registrationStatus || "approved",
            isConfirmed: true,
          };
        }

        let resolvedMessage = apiResult.message || "Attendance request finished.";

        if (status === "already_checked_in") {
          resolvedMessage = "This attendee has already been marked as attended.";
        } else if (status === "not-found") {
          resolvedMessage = INVALID_OR_NOT_FOUND_MESSAGE;
        }

        const resultItem: AttendanceResultItem = {
          id: `${Date.now()}-${resolvedRegistrationId}`,
          status,
          registrationId: resolvedRegistrationId,
          message: resolvedMessage,
          manualApprovalApplied,
          scannedAt: Date.now(),
          attended,
          attendedAt,
          attendee: {
            name: resolvedAttendee.name || null,
            email: resolvedAttendee.email,
            phoneNumber: resolvedAttendee.phoneNumber,
            profession: resolvedAttendee.profession,
            organization: resolvedAttendee.organization,
            designation: resolvedAttendee.designation,
            linkedinUrl: resolvedAttendee.linkedinUrl,
            tshirtSize: resolvedAttendee.tshirtSize,
            foodPreference: resolvedAttendee.foodPreference,
            nic: resolvedAttendee.nic,
          },
          registration: resolvedRegistrationInfo,
        };

        setCurrentResult(resultItem);

        if (isTrackableRecentStatus(status)) {
          appendRecentScan(resultItem);
        }

        setUiState(toUiState(status));

        if (status === "api-error") {
          invalidateStoredSecret(resultItem.message);
        }

        if (status === "success" || status === "already_checked_in") {
          const registryEntry = {
            attendee: resultItem.attendee,
            attendedAt: resultItem.attendedAt || new Date(resultItem.scannedAt).toISOString(),
            registration: resultItem.registration,
            registrationId: resultItem.registrationId,
          };

          attendedRegistryRef.current.set(resolvedRegistrationId, registryEntry);
          attendedRegistryRef.current.set(normalizedRegistrationId, registryEntry);
        }

        if (attended !== null) {
          const attendanceTime =
            attended === true
              ? apiResult.registration?.attended_at || attendedAt || new Date().toISOString()
              : null;

          const candidateRegistrationIds = new Set(
            [
              normalizedRegistrationId,
              resolvedRegistrationId,
              apiResult.registration?.id,
              apiResult.registrationId,
              resultItem.registrationId,
            ].filter((value): value is string => Boolean(value && value.trim())),
          );

          setAllRegistrations((current) => {
            return current.map((registration) => {
              if (!candidateRegistrationIds.has(registration.registration_id)) {
                return registration;
              }

              return {
                ...registration,
                status:
                  manualApprovalApplied
                    ? "approved"
                    : asOptionalTrimmedText(apiResult.registration?.status) || registration.status,
                is_confirmed:
                  manualApprovalApplied
                    ? true
                    : apiResult.registration?.is_confirmed ?? registration.is_confirmed,
                attended,
                attended_at: attendanceTime || registration.attended_at,
              };
            });
          });
        }
      } catch (error) {
        const message = getErrorMessage(error, "Failed to call attendance API. Please try again.");
        const resolvedRegistration = registrationsById.get(normalizedRegistrationId);

        invalidateStoredSecret(message);

        const resultItem: AttendanceResultItem = {
          id: `${Date.now()}-${normalizedRegistrationId}-error`,
          status: "api-error",
          registrationId: normalizedRegistrationId,
          message,
          manualApprovalApplied: false,
          scannedAt: Date.now(),
          attended: null,
          attendedAt: null,
          attendee: {
            name: preferredName?.trim() || resolvedRegistration?.name.trim() || null,
            email: resolvedRegistration?.email?.trim() || null,
            phoneNumber: resolvedRegistration?.phone_number?.trim() || null,
            profession: resolvedRegistration?.profession?.trim() || null,
            organization: resolvedRegistration?.organization?.trim() || null,
            designation: resolvedRegistration?.designation?.trim() || null,
            linkedinUrl: resolvedRegistration?.linkedin_url?.trim() || null,
            tshirtSize: resolvedRegistration?.tshirt_size?.trim() || null,
            foodPreference: resolvedRegistration?.food_preference?.trim() || null,
            nic: resolvedRegistration?.id_number?.trim() || null,
          },
          registration: {
            registrationStatus: asOptionalTrimmedText(resolvedRegistration?.status),
            isConfirmed: resolvedRegistration?.is_confirmed ?? null,
          },
        };

        setCurrentResult(resultItem);
        appendRecentScan(resultItem);
        setUiState("error");
      } finally {
        if (source === "search") {
          setSearchActionRegistrationId(null);
        }

        if (releaseLockTimeoutRef.current !== null) {
          window.clearTimeout(releaseLockTimeoutRef.current);
        }

        releaseLockTimeoutRef.current = window.setTimeout(() => {
          processingLockRef.current = false;
          setIsProcessing(false);
          releaseLockTimeoutRef.current = null;
        }, AUTO_SCAN_RESUME_DELAY_MS);
      }
    },
    [appendRecentScan, invalidateStoredSecret, registrationsById],
  );

  const handleQrDetected = useCallback(
    (rawValue: string) => {
      const normalizedRawValue = rawValue.trim();
      if (!normalizedRawValue || processingLockRef.current) {
        return;
      }

      const now = Date.now();
      const lastRawValue = lastQrValueRef.current;

      if (
        lastRawValue &&
        lastRawValue.value === normalizedRawValue &&
        now - lastRawValue.at < SCAN_COOLDOWN_MS
      ) {
        return;
      }

      lastQrValueRef.current = {
        value: normalizedRawValue,
        at: now,
      };

      const registrationId = extractRegistrationIdFromInput(normalizedRawValue);

      if (!registrationId) {
        setUiState("error");
        setCurrentResult({
          id: `${Date.now()}-invalid`,
          status: "invalid-qr",
          registrationId: null,
          message: INVALID_OR_NOT_FOUND_MESSAGE,
          manualApprovalApplied: false,
          scannedAt: Date.now(),
          attended: null,
          attendedAt: null,
          attendee: {
            name: null,
            email: null,
            phoneNumber: null,
            profession: null,
            organization: null,
            designation: null,
            linkedinUrl: null,
            tshirtSize: null,
            foodPreference: null,
            nic: null,
          },
          registration: {
            registrationStatus: null,
            isConfirmed: null,
          },
        });
        return;
      }

      void processAttendance(registrationId, "qr");
    },
    [processAttendance],
  );

  const handleSearchMarkAttendance = useCallback(
    (registration: AdminRegistration) => {
      if (processingLockRef.current || !registration.registration_id) {
        return;
      }

      setSearchActionRegistrationId(registration.registration_id);
      void processAttendance(registration.registration_id, "search", registration.name || null);
    },
    [processAttendance],
  );

  const handleOpenSearchDetails = useCallback((registration: AdminRegistration) => {
    setSelectedSearchRegistration(registration);
  }, []);

  const handleCloseSearchDetails = useCallback(() => {
    setSelectedSearchRegistration(null);
  }, []);

  const handleSecretSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedSecret = secretInput.trim();
    if (!trimmedSecret) {
      setSecretError("Please enter the admin secret.");
      return;
    }

    try {
      window.localStorage.setItem(LOCAL_STORAGE_ADMIN_SECRET_KEY, trimmedSecret);
      setSecretError(null);
      setSecretInput("");
      setAdminSecretPresent(true);
    } catch {
      setSecretError("Unable to save admin secret to local storage.");
    }
  };

  const handlePermissionDenied = useCallback(
    (_message: string) => {
      const permissionMessage = "Camera access is required to scan QR codes";
      setIsScannerActive(false);
      setUiState("error");
      setScannerMessage(permissionMessage);
      setCurrentResult({
        id: `${Date.now()}-camera-permission`,
        status: "camera-permission-denied",
        registrationId: null,
        message: permissionMessage,
        manualApprovalApplied: false,
        scannedAt: Date.now(),
        attended: null,
        attendedAt: null,
        attendee: {
          name: null,
          email: null,
          phoneNumber: null,
          profession: null,
          organization: null,
          designation: null,
          linkedinUrl: null,
          tshirtSize: null,
          foodPreference: null,
          nic: null,
        },
        registration: {
          registrationStatus: null,
          isConfirmed: null,
        },
      });
    },
    [],
  );

  const handleScannerError = useCallback((message: string) => {
    setScannerMessage(message);

    if (message) {
      setUiState("error");
    }
  }, []);

  const handleScannerStarted = useCallback(() => {
    if (!processingLockRef.current) {
      setUiState("scanning");
    }
  }, []);

  const handleScannerToggle = useCallback(() => {
    setScannerMessage(null);
    setIsScannerActive((current) => {
      const next = !current;

      if (next) {
        setUiState("scanner-starting");
      } else if (!isProcessing) {
        setUiState("idle");
      }

      return next;
    });
  }, [isProcessing]);

  const currentResultMeta = currentResult ? RESULT_CARD_META[currentResult.status] : null;
  const currentResultStatusMeta = currentResult ? STATUS_META[currentResult.status] : null;
  const currentResultMessage =
    currentResult && currentResultMeta
      ? (currentResult.status === "invalid-qr" || currentResult.status === "not-found"
          ? currentResultMeta.defaultMessage
          : currentResult.message || currentResultMeta.defaultMessage)
      : null;
  const showResultDetails =
    Boolean(currentResult) &&
    (currentResult?.status === "success" || currentResult?.status === "already_checked_in");
  const uiMeta = UI_STATE_META[uiState];

  const searchHintText = debouncedSearchTerm
    ? searchResults.length
      ? null
      : "No attendees found for that name."
    : "Type at least part of a name to search attendees.";

  const guestListRows = useMemo(() => {
    const persistedRows: AttendanceResultItem[] = allRegistrations
      .filter((registration) => registration.attended === true || Boolean(registration.attended_at))
      .map((registration) => {
        const scannedAt = parseTimestamp(registration.attended_at, Date.now());

        return {
          id: `persisted-${registration.registration_id}`,
          status: "success",
          registrationId: registration.registration_id,
          message: "Attendance recorded.",
          manualApprovalApplied: false,
          scannedAt,
          attended: registration.attended ?? true,
          attendedAt: registration.attended_at || null,
          attendee: {
            name: registration.name || null,
            email: registration.email || null,
            phoneNumber: registration.phone_number || null,
            profession: registration.profession || null,
            organization: registration.organization || null,
            designation: registration.designation || null,
            linkedinUrl: registration.linkedin_url || null,
            tshirtSize: registration.tshirt_size || null,
            foodPreference: registration.food_preference || null,
            nic: registration.id_number || null,
          },
          registration: {
            registrationStatus: asOptionalTrimmedText(registration.status),
            isConfirmed: registration.is_confirmed ?? null,
          },
        };
      });

    const mergedRowsByRegistrationId = new Map<string, AttendanceResultItem>();

    persistedRows.forEach((row) => {
      if (!row.registrationId) {
        return;
      }

      mergedRowsByRegistrationId.set(row.registrationId, row);
    });

    recentScans
      .filter(
        (scan) =>
          Boolean(scan.registrationId) &&
          (scan.status === "success" || scan.status === "already_checked_in"),
      )
      .forEach((scan) => {
        if (!scan.registrationId) {
          return;
        }

        const existing = mergedRowsByRegistrationId.get(scan.registrationId);
        if (!existing || scan.scannedAt >= existing.scannedAt) {
          mergedRowsByRegistrationId.set(scan.registrationId, {
            ...(existing || {}),
            ...scan,
            id: existing?.id || scan.id,
          });
        }
      });

    return Array.from(mergedRowsByRegistrationId.values()).sort((left, right) => {
      const leftTime = parseTimestamp(left.attendedAt, left.scannedAt);
      const rightTime = parseTimestamp(right.attendedAt, right.scannedAt);

      return rightTime - leftTime;
    });
  }, [allRegistrations, recentScans]);

  const guestTshirtSizeOptions = useMemo(() => {
    const dynamicOptions = Array.from(
      new Set(
        allRegistrations
          .map((registration) => normalizeTshirtSize(registration.tshirt_size))
          .filter((value) => Boolean(value)),
      ),
    ).sort((left, right) => {
      return left.localeCompare(right, undefined, { sensitivity: "base" });
    });

    return mergeFilterOptions(DEFAULT_TSHIRT_SIZE_OPTIONS, dynamicOptions);
  }, [allRegistrations]);

  const guestFoodPreferenceOptions = useMemo(() => {
    const dynamicOptions = buildGuestFilterOptions(
      allRegistrations.map((registration) => registration.food_preference),
    );

    return mergeFilterOptions(DEFAULT_FOOD_PREFERENCE_OPTIONS, dynamicOptions);
  }, [allRegistrations]);

  const filteredGuestListRows = useMemo(() => {
    const normalizedSearchTerm = guestSearchTerm.trim().toLowerCase();
    const normalizedTshirtFilter = normalizeTshirtSize(guestTshirtFilter);
    const normalizedFoodPreferenceFilter = normalizeFoodPreference(guestFoodPreferenceFilter);

    return guestListRows.filter((scan) => {
      const name = (scan.attendee.name || "").toLowerCase();
      const tshirtSize = normalizeTshirtSize(scan.attendee.tshirtSize);
      const foodPreference = normalizeFoodPreference(scan.attendee.foodPreference);

      const matchesSearch = !normalizedSearchTerm || name.includes(normalizedSearchTerm);
      const matchesTshirt =
        guestTshirtFilter === ALL_TSHIRT_SIZES || tshirtSize === normalizedTshirtFilter;
      const matchesFoodPreference =
        guestFoodPreferenceFilter === ALL_FOOD_PREFERENCES ||
        foodPreference === normalizedFoodPreferenceFilter;

      return matchesSearch && matchesTshirt && matchesFoodPreference;
    });
  }, [guestFoodPreferenceFilter, guestListRows, guestSearchTerm, guestTshirtFilter]);

  const hasActiveGuestFilters =
    Boolean(guestSearchTerm.trim()) ||
    guestTshirtFilter !== ALL_TSHIRT_SIZES ||
    guestFoodPreferenceFilter !== ALL_FOOD_PREFERENCES;

  const hasFilteredGuestRowsForExport = filteredGuestListRows.length > 0;

  const handleDownloadGuestListCsv = useCallback(() => {
    if (!hasFilteredGuestRowsForExport) {
      return;
    }

    const csvRows = filteredGuestListRows.map((scan) => {
      const formattedFoodPreference = formatFoodPreference(scan.attendee.foodPreference);

      return [
        scan.attendee.name || "",
        scan.attendee.email || "",
        scan.attendee.phoneNumber || "",
        formatRegistrationStatusForCsv(scan.registration.registrationStatus),
        formatConfirmationStatusForCsv(scan.registration.isConfirmed),
        formatSriLankaTimeForCsv(scan.attendedAt || scan.scannedAt),
        formatAttendanceStatusForCsv(scan.attended),
        scan.attendee.tshirtSize || "",
        formattedFoodPreference === "-" ? "" : formattedFoodPreference,
        scan.attendee.nic || "",
        scan.attendee.profession || "",
        scan.attendee.organization || "",
        scan.attendee.designation || "",
        scan.attendee.linkedinUrl || "",
      ];
    });

    const csvData = [GUEST_CSV_EXPORT_HEADERS, ...csvRows]
      .map((row) => row.map((value) => formatCsvValue(value)).join(","))
      .join("\n");

    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "attended_guest_list.csv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }, [filteredGuestListRows, hasFilteredGuestRowsForExport]);

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600&display=swap"
        rel="stylesheet"
      />

      <main className="itpro-admin-page" id="itpro-attendance-page">
        <div className="itpro-admin-orb orb-1" aria-hidden="true" />
        <div className="itpro-admin-orb orb-2" aria-hidden="true" />
        <div className="itpro-admin-bg-overlay" aria-hidden="true" />

        <div className="itpro-admin-wrapper">
          <div className="itpro-admin-logo-wrap">
            <img
              src={getPublicAssetPath("assets/img/globalazure2026.png")}
              alt="Global Azure Bootcamp Logo"
              className="itpro-admin-logo"
            />
          </div>

          <section className="itpro-admin-shell">
            <header className="itpro-admin-header">
              <div className="itpro-admin-badge">
                <span className="itpro-admin-badge-dot" /> IT Pro Admin
              </div>
              <h1>Attendance Scanner</h1>
              <p className="itpro-admin-subtitle">
                Scan attendee QR code to mark attendance
              </p>
            </header>

            {!adminSecretPresent ? (
              <div className="admin-secret-card">
                <h2>Enter admin secret</h2>
                <p>
                  To access the attendance scanner, enter your
                  <strong> Admin Key</strong>
                </p>
                {secretError ? (
                  <div className="admin-alert error" role="alert">
                    {secretError}
                  </div>
                ) : null}
                <form className="admin-secret-form" onSubmit={handleSecretSubmit}>
                  <input
                    className="admin-secret-input"
                    type="password"
                    value={secretInput}
                    onChange={(event) => {
                      setSecretInput(event.target.value);
                      if (secretError) {
                        setSecretError(null);
                      }
                    }}
                    placeholder="Admin secret"
                    aria-label="Admin secret"
                  />
                  <button className="admin-secret-submit" type="submit">
                    Save secret
                  </button>
                </form>
              </div>
            ) : (
              <>
                <div className={`attendance-state-pill ${uiMeta.className}`}>
                  <span className="attendance-state-dot" aria-hidden="true" />
                  State: {uiMeta.label}
                </div>

                <div className="attendance-admin-grid">
                  <section className="attendance-panel" aria-label="QR scanner panel">
                    <div className="attendance-scanner-head">
                      <div className="attendance-scanner-head-content">
                        <h2 className="attendance-scanner-title">Scanner</h2>
                        <p className="attendance-scanner-subtitle">
                          Scan QR codes and automatically check in attendees.
                        </p>
                      </div>
                      <button
                        type="button"
                        className={`attendance-action-button attendance-scanner-toggle ${
                          isScannerActive ? "attendance-action-stop" : "attendance-action-start"
                        }`}
                        onClick={handleScannerToggle}
                      >
                        <i
                          className={`bi ${isScannerActive ? "bi-stop-fill" : "bi-play-fill"}`}
                          aria-hidden="true"
                        />
                        {isScannerActive ? "Stop" : "Start"}
                      </button>
                    </div>

                    <div
                      className={`attendance-scanner-stage ${
                        isScannerActive
                          ? "attendance-scanner-stage-active"
                          : "attendance-scanner-stage-idle"
                      }`}
                    >
                      <QrScanner
                        isRunning={isScannerActive}
                        onDetected={handleQrDetected}
                        onPermissionDenied={handlePermissionDenied}
                        onScannerError={handleScannerError}
                        onScannerStarted={handleScannerStarted}
                      />
                    </div>

                    {scannerMessage ? (
                      <div className="admin-alert error attendance-inline-alert" role="alert">
                        {scannerMessage}
                      </div>
                    ) : null}

                    {isProcessing ? (
                      <div className="admin-table-state attendance-processing-state" role="status">
                        <span className="admin-spinner" aria-hidden="true" />
                        Verifying and marking attendance...
                      </div>
                    ) : null}

                    <section className="attendance-search-block" aria-label="Manual attendee search">
                      <h3>Search attendee manually</h3>
                      <div className="attendance-search-input-wrap">
                        <i className="bi bi-search" aria-hidden="true" />
                        <input
                          className="attendance-search-input"
                          type="text"
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                          placeholder="Search by attendee name"
                          disabled={isProcessing || isLoadingRegistrations}
                        />
                      </div>

                      {isLoadingRegistrations ? (
                        <div className="admin-table-state attendance-search-state" role="status">
                          <span className="admin-spinner" aria-hidden="true" />
                          Loading attendees...
                        </div>
                      ) : null}

                      {searchError ? (
                        <div className="admin-alert error attendance-inline-alert" role="alert">
                          {searchError}
                        </div>
                      ) : null}

                      {!isLoadingRegistrations && !searchError && searchHintText ? (
                        <p className="attendance-search-hint">{searchHintText}</p>
                      ) : null}

                      {!isLoadingRegistrations && !searchError && searchResults.length ? (
                        <ul className="attendance-search-results">
                          {searchResults.map((registration) => {
                            const isActionLoading =
                              isProcessing &&
                              searchActionRegistrationId === registration.registration_id;
                            const isAlreadyAttended = registration.attended === true;
                            const actionLabel = isActionLoading
                              ? "Marking..."
                              : isAlreadyAttended
                                ? "Already Attended"
                                : "Mark as Attended";

                            return (
                              <li key={registration.registration_id} className="attendance-search-item">
                                <div className="attendance-search-item-main">
                                  <p className="attendance-search-name">{registration.name || "-"}</p>
                                  <p className="attendance-search-email">{registration.email || "-"}</p>

                                  <dl className="attendance-search-summary">
                                    <div className="attendance-search-summary-item">
                                      <dt>Approval Status</dt>
                                      <dd>{formatRegistrationStatus(registration.status)}</dd>
                                    </div>
                                    <div className="attendance-search-summary-item">
                                      <dt>Confirmation Status</dt>
                                      <dd>{formatConfirmationStatus(registration.is_confirmed)}</dd>
                                    </div>
                                  </dl>
                                </div>

                                <div className="attendance-search-actions">
                                  <button
                                    type="button"
                                    className={`attendance-action-button ${
                                      isAlreadyAttended
                                        ? "attendance-action-attended"
                                        : "attendance-action-submit"
                                    }`}
                                    onClick={() => handleSearchMarkAttendance(registration)}
                                    disabled={isProcessing || isAlreadyAttended}
                                  >
                                    {actionLabel}
                                  </button>

                                  <button
                                    type="button"
                                    className="attendance-action-button attendance-action-view"
                                    onClick={() => handleOpenSearchDetails(registration)}
                                    aria-label={`View details for ${registration.name || "attendee"}`}
                                    title="View details"
                                  >
                                    <i className="bi bi-eye" aria-hidden="true" />
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      ) : null}
                    </section>
                  </section>

                  <section
                    className="attendance-panel attendance-results-panel"
                    aria-label="Attendance results panel"
                  >
                    <div className="attendance-panel-header attendance-panel-header-tight">
                      <h2>Scan result</h2>
                      <Link to="/itproadmin" className="attendance-back-link">
                        Back to approvals
                      </Link>
                    </div>

                    {currentResult && currentResultMeta ? (
                      <article className={`attendance-result-card ${currentResultMeta.cardClassName}`}>
                        <header className="attendance-result-header">
                          <div className="attendance-result-heading">
                            <span
                              className={`attendance-result-status-icon ${
                                currentResultStatusMeta?.className || ""
                              }`}
                              aria-hidden="true"
                            >
                              <i className={`bi ${currentResultMeta.icon}`} />
                            </span>
                            <h3>{currentResultMeta.title}</h3>
                          </div>

                          {currentResultMessage ? (
                            <p className="attendance-result-message">{currentResultMessage}</p>
                          ) : null}

                          {currentResultMeta.helperText ? (
                            <p className="attendance-result-helper-text">
                              {currentResultMeta.helperText}
                            </p>
                          ) : null}
                        </header>

                        {showResultDetails ? (
                          <section className="attendance-result-body">
                            <p className="attendance-result-name-hero">
                              {currentResult.attendee.name || "-"}
                            </p>

                            <dl className="attendance-result-detail-list">
                              <div className="attendance-result-detail-item">
                                <dt>Email</dt>
                                <dd>{currentResult.attendee.email || "-"}</dd>
                              </div>
                              <div className="attendance-result-detail-item">
                                <dt>Profession</dt>
                                <dd>{currentResult.attendee.profession || "-"}</dd>
                              </div>
                              <div className="attendance-result-detail-item">
                                <dt>Organization</dt>
                                <dd>{currentResult.attendee.organization || "-"}</dd>
                              </div>
                              <div className="attendance-result-detail-item">
                                <dt>T-shirt size</dt>
                                <dd>{currentResult.attendee.tshirtSize || "-"}</dd>
                              </div>
                              <div className="attendance-result-detail-item">
                                <dt>Food preference</dt>
                                <dd>{currentResult.attendee.foodPreference || "-"}</dd>
                              </div>
                              <div className="attendance-result-detail-item">
                                <dt>Registration Status</dt>
                                <dd>
                                  {formatRegistrationStatus(
                                    currentResult.registration.registrationStatus,
                                  )}
                                </dd>
                              </div>
                              <div className="attendance-result-detail-item">
                                <dt>Confirmation Status</dt>
                                <dd>
                                  {formatConfirmationStatus(currentResult.registration.isConfirmed)}
                                </dd>
                              </div>
                              <div className="attendance-result-detail-item">
                                <dt>Attendance Status</dt>
                                <dd>{formatAttendanceStatus(currentResult.attended)}</dd>
                              </div>
                              <div className="attendance-result-detail-item">
                                <dt>Checked-in Time</dt>
                                <dd>
                                  {currentResult.attended === true
                                    ? formatSriLankaTime(
                                        currentResult.attendedAt || currentResult.scannedAt,
                                      )
                                    : "-"}
                                </dd>
                              </div>
                            </dl>
                          </section>
                        ) : null}
                      </article>
                    ) : (
                      <div className="admin-table-state attendance-empty-result" role="status">
                        Scan a QR or mark attendance from search results.
                      </div>
                    )}
                  </section>
                </div>

                <section className="attendance-panel attendance-recent-panel" aria-label="Guest List">
                  <div className="attendance-panel-header attendance-panel-header-tight">
                    <h2>Guest List</h2>
                  </div>

                  <div className="attendance-guest-toolbar">
                    <div className="attendance-guest-search-wrap">
                      <i className="bi bi-search" aria-hidden="true" />
                      <input
                        className="attendance-guest-search-input"
                        type="text"
                        value={guestSearchTerm}
                        onChange={(event) => setGuestSearchTerm(event.target.value)}
                        placeholder="Search guest by name"
                        aria-label="Search guest by name"
                      />
                    </div>

                    <div className="attendance-guest-filter-control">
                      <label
                        className="attendance-guest-control-label"
                        htmlFor="attendance-guest-tshirt-filter"
                      >
                        T-shirt size
                      </label>
                      <div className="admin-filter-select-wrap attendance-guest-select-wrap">
                        <i className="bi bi-funnel" aria-hidden="true" />
                        <select
                          id="attendance-guest-tshirt-filter"
                          className="admin-filter-select"
                          value={guestTshirtFilter}
                          onChange={(event) => setGuestTshirtFilter(event.target.value)}
                          aria-label="Filter guest list by T-shirt size"
                        >
                          <option value={ALL_TSHIRT_SIZES}>All T-shirt sizes</option>
                          {guestTshirtSizeOptions.map((option) => {
                            return (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    <div className="attendance-guest-filter-control">
                      <label
                        className="attendance-guest-control-label"
                        htmlFor="attendance-guest-food-preference-filter"
                      >
                        Food Preference
                      </label>
                      <div className="admin-filter-select-wrap attendance-guest-select-wrap">
                        <i className="bi bi-funnel" aria-hidden="true" />
                        <select
                          id="attendance-guest-food-preference-filter"
                          className="admin-filter-select"
                          value={guestFoodPreferenceFilter}
                          onChange={(event) => setGuestFoodPreferenceFilter(event.target.value)}
                          aria-label="Filter guest list by food preference"
                        >
                          <option value={ALL_FOOD_PREFERENCES}>All Food Preferences</option>
                          {guestFoodPreferenceOptions.map((option) => {
                            return (
                              <option key={option} value={option}>
                                {formatFoodPreference(option)}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    <div className="attendance-guest-toolbar-actions">
                      <button
                        type="button"
                        className="admin-download-csv-button attendance-guest-download-button"
                        onClick={handleDownloadGuestListCsv}
                        disabled={!hasFilteredGuestRowsForExport}
                        title={
                          hasFilteredGuestRowsForExport
                            ? "Download filtered guest list as CSV"
                            : "No attended participants available for export."
                        }
                      >
                        <i className="bi bi-download" aria-hidden="true" />
                        <span>Download CSV</span>
                      </button>
                    </div>
                  </div>

                  <div className="attendance-recent">
                    {!guestListRows.length ? (
                      <p className="attendance-recent-empty">No checked-in attendees yet.</p>
                    ) : !filteredGuestListRows.length ? (
                      <p className="attendance-recent-empty">
                        {hasActiveGuestFilters
                          ? "No guests match current search or filters."
                          : "No checked-in attendees yet."}
                      </p>
                    ) : (
                      <div className="attendance-recent-table-wrap">
                        <table className="attendance-recent-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Approval Status</th>
                              <th>Confirmation Status</th>
                              <th>Check-in Time</th>
                              <th>Attendance Status</th>
                              <th>T-shirt size</th>
                              <th>Food Preference</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredGuestListRows.map((scan, index) => {
                              return (
                                <tr key={scan.id}>
                                  <td className="attendance-recent-index">{index + 1}</td>
                                  <td className="attendance-recent-name">{scan.attendee.name || "-"}</td>
                                  <td className="attendance-recent-email">{scan.attendee.email || "-"}</td>
                                  <td>
                                    {formatRegistrationStatus(scan.registration.registrationStatus)}
                                  </td>
                                  <td>{formatConfirmationStatus(scan.registration.isConfirmed)}</td>
                                  <td>{formatSriLankaTime(scan.attendedAt || scan.scannedAt)}</td>
                                  <td>{formatAttendanceStatus(scan.attended)}</td>
                                  <td>{scan.attendee.tshirtSize || "-"}</td>
                                  <td>{formatFoodPreference(scan.attendee.foodPreference)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </section>

                {selectedSearchRegistration ? (
                  <div
                    className="modal-overlay attendance-details-modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="attendance-details-modal-title"
                    onClick={handleCloseSearchDetails}
                  >
                    <div
                      className="modal-content attendance-details-modal"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="modal-header">
                        <div className="modal-title-wrapper">
                          <div className="modal-icon-bg">
                            <i className="bi bi-person-vcard modal-title-icon" aria-hidden="true" />
                          </div>
                          <h2 id="attendance-details-modal-title">Attendee Details</h2>
                        </div>

                        <button
                          type="button"
                          className="modal-close-button"
                          onClick={handleCloseSearchDetails}
                          aria-label="Close details modal"
                        >
                          <span aria-hidden="true">×</span>
                        </button>
                      </div>

                      <div className="modal-body">
                        <div className="detail-cards-container">
                          <div className="detail-card">
                            <div className="detail-card-header">
                              <h3>Personal Information</h3>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Full Name</span>
                              <span className="detail-value highlight">
                                {selectedSearchRegistration.name || "-"}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Email Address</span>
                              <span className="detail-value">
                                {selectedSearchRegistration.email || "-"}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Phone Number</span>
                              <span className="detail-value">
                                {selectedSearchRegistration.phone_number || "-"}
                              </span>
                            </div>
                          </div>

                          <div className="detail-card">
                            <div className="detail-card-header">
                              <h3>Professional Details</h3>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Profession</span>
                              <span className="detail-value">
                                {selectedSearchRegistration.profession || "-"}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Organization</span>
                              <span className="detail-value">
                                {selectedSearchRegistration.organization || "-"}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Designation</span>
                              <span className="detail-value">
                                {selectedSearchRegistration.designation || "-"}
                              </span>
                            </div>
                          </div>

                          <div className="detail-card full-span">
                            <div className="detail-card-header">
                              <h3>Attendance & Registration</h3>
                            </div>
                            <div className="detail-grid-2">
                              <div className="detail-row">
                                <span className="detail-label">Approval Status</span>
                                <span className="detail-value">
                                  {formatRegistrationStatus(selectedSearchRegistration.status)}
                                </span>
                              </div>
                              <div className="detail-row">
                                <span className="detail-label">Confirmation Status</span>
                                <span className="detail-value">
                                  {formatConfirmationStatus(selectedSearchRegistration.is_confirmed)}
                                </span>
                              </div>
                              <div className="detail-row">
                                <span className="detail-label">Attendance Status</span>
                                <span className="detail-value">
                                  {formatAttendanceStatus(selectedSearchRegistration.attended)}
                                </span>
                              </div>
                              <div className="detail-row">
                                <span className="detail-label">Checked-in Time</span>
                                <span className="detail-value">
                                  {selectedSearchRegistration.attended === true
                                    ? formatSriLankaTime(selectedSearchRegistration.attended_at)
                                    : "-"}
                                </span>
                              </div>
                              <div className="detail-row">
                                <span className="detail-label">Food Preference</span>
                                <span className="detail-value">
                                  {selectedSearchRegistration.food_preference || "-"}
                                </span>
                              </div>
                              <div className="detail-row">
                                <span className="detail-label">T-shirt Size</span>
                                <span className="detail-value">
                                  {selectedSearchRegistration.tshirt_size || "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </section>

          <p className="itpro-admin-disclaimer">
            Your data is secure · Attendance operations are logged
          </p>
        </div>
      </main>
    </>
  );
};

export default ItProAttendancePage;
